/**
 * What a package is, in one place (P25009-403).
 *
 * A "package" is a Bygg paket listing: a real Sharetribe listing that a buyer
 * sees and buys as ONE product ("Komplett hjulsats Volvo XC60"), but which is
 * fulfilled from the seller's ordinary rim and tyre listings. The link is
 * `publicData.reachBundle`, written by services/packageReachPublisher.js.
 *
 * The rule this module exists to protect (BR-1): a package is one purchase
 * unit. It is one line, one price, one delivery. Its parts travel with the
 * order so their stock can be drawn down and so the buyer, the seller and the
 * accounting export can see WHAT is in the package — but the parts are never
 * priced again. The package price already covers them, so pricing them a
 * second time would charge the buyer twice.
 *
 * That is why package parts are deliberately kept out of `cartListings`:
 * everything in `cartListings` becomes a priced `line-item/cart-item` row
 * (see api-util/lineItems.js). Package parts go into `packageParts` instead,
 * which is a stock-and-display list with no price of its own.
 *
 * Two bundle shapes are read here. The one on every listing published so far:
 *
 *   reachBundle: { rimListingId, rimQty, tireListingId, tireQty, addonListingIds }
 *
 * and the typed list the package-types story (P25009-399) will write, which
 * has to exist for tyre-only and rim-only packages and for staggered cars
 * where the front and rear parts differ:
 *
 *   reachBundle: { parts: [{ listingId, quantity, kind, role, title }] }
 *
 * Reading both now means P25009-399 can switch the writer over without
 * touching checkout, and packages published today keep working.
 *
 * Written as CommonJS ON PURPOSE, same as src/util/csvCore.js and for the same
 * reason: it is shared by the browser bundle and by the plain-Node API server,
 * which is run directly with `node` and is not transpiled, so it cannot import
 * an ES module. Keep it free of any browser- or Node-only API. One copy rather
 * than a client twin and a server twin, because "a package is one purchase
 * unit" has to mean the same thing on both sides of the wire.
 */

/** A part quantity is a count of physical items; anything else is a bug. */
const toQuantity = value => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const idOf = value => (value && value.uuid) || value || null;

const clean = value => (typeof value === 'string' ? value.trim() : '');

/**
 * The bundle block off a listing, however the listing was handed to us —
 * a full Sharetribe entity, a plain JSON-API resource, or just publicData.
 *
 * @param {Object} source listing entity, JSON-API resource, or publicData
 * @returns {Object|null} the reachBundle, or null when this is not a package
 */
const readReachBundle = source => {
  if (!source || typeof source !== 'object') return null;
  const publicData = source.attributes?.publicData || source.publicData || source;
  const bundle = publicData?.reachBundle;
  return bundle && typeof bundle === 'object' ? bundle : null;
};

/**
 * The parts of a package, normalised into one shape.
 *
 * `quantity` is per package: a complete wheelset is 4 rims and 4 tyres, so
 * ordering two packages draws 8 of each (see multiplyPartQuantities).
 *
 * `role` is 'all' for a square car, or 'front' / 'rear' for a staggered one
 * where the two axles take different sizes. `kind` is what the part is, which
 * is what lets the sub-rows read "4 × fälg …" rather than a bare title.
 *
 * @param {Object} source listing entity, JSON-API resource, or publicData
 * @returns {Array<{listingId:string, quantity:number, kind:string, role:string, title:string}>}
 */
const packagePartsOf = source => {
  const bundle = readReachBundle(source);
  if (!bundle) return [];

  // Typed list first — when P25009-399 writes it, it is the complete truth and
  // the legacy rim/tyre fields below are not read at all.
  if (Array.isArray(bundle.parts)) {
    return bundle.parts
      .map(part => ({
        listingId: idOf(part?.listingId),
        quantity: toQuantity(part?.quantity ?? part?.qty),
        kind: clean(part?.kind) || 'part',
        role: clean(part?.role) || 'all',
        title: clean(part?.title),
      }))
      .filter(part => part.listingId && part.quantity > 0);
  }

  const legacy = [
    { listingId: idOf(bundle.rimListingId), quantity: toQuantity(bundle.rimQty), kind: 'rim' },
    { listingId: idOf(bundle.tireListingId), quantity: toQuantity(bundle.tireQty), kind: 'tire' },
  ];

  /* Add-ons in the legacy shape are bare ids with no quantity of their own.
   * They are per-wheel items (bolts, sensors), so they follow the rim count —
   * the same "one per wheel" rule the publisher used when it built the bundle. */
  const addonQty = toQuantity(bundle.rimQty);
  const addons = (Array.isArray(bundle.addonListingIds) ? bundle.addonListingIds : [])
    .map(id => ({ listingId: idOf(id), quantity: addonQty, kind: 'addon' }));

  return [...legacy, ...addons]
    .filter(part => part.listingId && part.quantity > 0)
    .map(part => ({ ...part, role: 'all', title: '' }));
};

/**
 * Is this listing a package?
 *
 * Both halves are required on purpose. `isReachShell` is the marker the
 * publisher writes and the search and admin surfaces filter on, but a listing
 * carrying the marker with no usable bundle cannot be fulfilled, and treating
 * it as a package would produce an order with nothing to draw stock from. Such
 * a listing falls back to being an ordinary single-item listing, which is the
 * safe direction to fail in.
 *
 * @param {Object} source listing entity, JSON-API resource, or publicData
 * @returns {boolean}
 */
const isPackageListing = source => {
  if (!source || typeof source !== 'object') return false;
  const publicData = source.attributes?.publicData || source.publicData || source;
  if (publicData?.isReachShell !== true) return false;
  return packagePartsOf(source).length > 0;
};

/**
 * Scale the per-package quantities by how many packages are being bought.
 *
 * @param {Array} parts from packagePartsOf
 * @param {number} packageCount the order quantity (stockReservationQuantity)
 * @returns {Array} the same parts with `quantity` multiplied
 */
const multiplyPartQuantities = (parts = [], packageCount = 1) => {
  const count = toQuantity(packageCount) || 1;
  return (Array.isArray(parts) ? parts : []).map(part => ({
    ...part,
    quantity: part.quantity * count,
  }));
};

/**
 * How many whole packages the parts on hand can make.
 *
 * This is the number that belongs on the package listing as its own stock.
 * A package is only real while EVERY part is there, so the limiting part
 * decides — 12 rims and 4 tyres is one wheelset, not three.
 *
 * A part whose stock we could not read counts as zero rather than unlimited:
 * over-reporting here sells a package the seller cannot ship.
 *
 * @param {Array} parts from packagePartsOf
 * @param {Map|Object} stockByListingId listingId -> quantity on hand
 * @returns {number} whole packages available, never negative
 */
const packagesAvailable = (parts, stockByListingId) => {
  const list = Array.isArray(parts) ? parts : [];
  if (list.length === 0) return 0;

  const lookup = id =>
    stockByListingId instanceof Map ? stockByListingId.get(id) : (stockByListingId || {})[id];

  return list.reduce((fewest, part) => {
    const onHand = Number(lookup(part.listingId));
    const possible = Number.isFinite(onHand) ? Math.floor(onHand / part.quantity) : 0;
    return Math.min(fewest, Math.max(0, possible));
  }, Number.POSITIVE_INFINITY);
};

/* What each kind of part is called, singular and plural. A package's sub-rows
 * read "4 × fälgar", not "4 × rim" — the buyer is Swedish and the seller reads
 * the same rows in their dashboard. */
const PART_KIND_LABELS = {
  rim: ['fälg', 'fälgar'],
  tire: ['däck', 'däck'],
  addon: ['tillbehör', 'tillbehör'],
  part: ['del', 'delar'],
};

/* Which axle, when the two differ. Left off entirely for a square car, where
 * saying "fram" and "bak" about identical wheels is noise. */
const PART_ROLE_LABELS = { front: 'fram', rear: 'bak' };

/**
 * One sub-row of a package, as a sentence: "4 × fälgar · BROCK B41 17\"".
 *
 * The title is whatever the part listing is called and may be missing on a
 * package published before titles were carried in the bundle; the count and the
 * kind are always known, so the row is never bare.
 *
 * @param {Object} part from packagePartsOf, or a protectedData.packageParts entry
 * @returns {string}
 */
const packagePartLabel = part => {
  const quantity = toQuantity(part?.quantity) || 1;
  const [singular, plural] = PART_KIND_LABELS[part?.kind] || PART_KIND_LABELS.part;
  const kind = quantity === 1 ? singular : plural;
  const role = PART_ROLE_LABELS[part?.role] || '';
  const head = `${quantity} × ${[kind, role].filter(Boolean).join(' ')}`;
  const title = clean(part?.title);
  return title ? `${head} · ${title}` : head;
};

module.exports = {
  isPackageListing,
  readReachBundle,
  packagePartsOf,
  multiplyPartQuantities,
  packagesAvailable,
  packagePartLabel,
};
